// Astrea Ops Platform - Supabase client
//
// Phase 3 scaffolding. Not yet imported anywhere in src/App.jsx - this is the client
// module the tab-by-tab conversion will import from, starting with Overview/Event
// Form/Tasks per the go-live plan. Uses the publishable key only (safe for the
// browser bundle) - RLS on every table (20260915000007_rls_policies.sql) is what
// actually governs what a signed-in user can read or write, the same way the app
// already relies on canSeeFinance/canSeeFiles today, just database-enforced now
// instead of UI-enforced.
//
// SUPABASE_SECRET_KEY (the service-role-equivalent key that bypasses RLS) must never
// be imported here or anywhere else that ships to the browser - it belongs only in
// Vercel serverless functions, same pattern as RESEND_API_KEY in
// api/send-notification.js. The Phase 5 import script uses it directly from
// .env.local for exactly that reason: it's a one-time server-side script, not app code.

import { createClient } from '@supabase/supabase-js';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseKey = process.env.REACT_APP_SUPABASE_PUBLISHABLE_KEY;

if (!supabaseUrl || !supabaseKey) {
  // Fails loudly at build/start time rather than surfacing as a confusing runtime
  // error deep inside a data call - matches how this file will actually get used,
  // imported once near the top of the app rather than lazily.
  throw new Error(
    'Missing REACT_APP_SUPABASE_URL or REACT_APP_SUPABASE_PUBLISHABLE_KEY. ' +
    'Both must be set in .env.local (see the Supabase project Settings > API Keys).'
  );
}

export const supabase = createClient(supabaseUrl, supabaseKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
  },
});

// Phase 2 helper: the app's own role check, mirroring current_app_role() in
// 20260915000006_auth_helpers.sql. Call after sign-in to get the signed-in user's
// profile (role, person_id, mfa_required, password_changed_at) in one round trip.
export async function getCurrentProfile() {
  const { data: { user }, error: authError } = await supabase.auth.getUser();
  if (authError || !user) return { profile: null, error: authError };
  const { data, error } = await supabase
    .from('profiles')
    .select('id, person_id, role, mfa_required, password_changed_at, people(name, email)')
    .eq('id', user.id)
    .single();
  return { profile: data, error };
}

// Phase 2: forced-rotation check, mirroring is_password_stale() in the same
// migration - call this right after getCurrentProfile() and route to a reset flow
// if it comes back true, rather than reimplementing the 90/180-day window here.
export async function isCurrentPasswordStale() {
  const { data, error } = await supabase.rpc('is_password_stale', {
    p_profile_id: (await supabase.auth.getUser()).data.user?.id,
  });
  if (error) return false; // fail open to "not stale" rather than lock someone out on an RPC error
  return !!data;
}
